/**
 * TimesheetPage - Container fuer Timer-basierte Zeiterfassung
 *
 * WICHTIG: Teamerfasser koennen NUR den heutigen Tag bearbeiten!
 * Keine Datumsauswahl - Force Today.
 *
 * Step-Logic:
 * 1. Erster Start (keine Entries) → RosterSelector (Anwesenheits-Auswahl)
 * 2. Timer laeuft → StopwatchControls + ActiveCrewList
 * 3. Pause (Entries vorhanden, keine RUNNING) → IdleControls + ActiveCrewList
 * 4. Tag abgeschlossen (taLocked) → Summary/Readonly View
 */

import { useEffect } from 'preact/hooks';
import { computed } from '@preact/signals';
import { Loader2, Lock } from 'lucide-preact';
import {
  initStore,
  hasRunningEntries,
  isLocked,
  isLoading,
  isInitialized,
  entries,
} from '../../stores/timesheet.store';
import { RosterSelector } from './RosterSelector';
import { StopwatchControls } from './StopwatchControls';
import { IdleControls } from './IdleControls';
import { ActiveCrewList } from './ActiveCrewList';
import { SyncStatusIndicator } from './SyncStatusIndicator';
import { RefreshButton } from './RefreshButton';
import { GpsIndicator } from './GpsIndicator';
import styles from './TimesheetPage.module.css';

export function TimesheetPage() {
  // Store initialisieren - IMMER nur fuer heute
  useEffect(() => {
    void initStore();
  }, []);

  // Bestimme welcher Step angezeigt wird
  // locked: Tag abgeschlossen
  // running: Timer laeuft
  // idle: Entries vorhanden, aber alle gestoppt (Pause)
  // roster: Erster Start des Tages (keine Entries)
  const currentStep = computed(() => {
    if (isLocked.value) return 'locked';
    if (hasRunningEntries.value) return 'running';
    if (entries.value.length > 0) return 'idle'; // Nach Stop, vor neuem Start oder Tagesabschluss
    return 'roster';
  });

  // Operativer Status fuer Header (sprechende Texte statt DRAFT/SUBMITTED)
  const operationalStatus = computed(() => {
    if (isLocked.value) {
      return { label: 'Tag abgeschlossen', variant: 'locked' as const };
    }
    if (hasRunningEntries.value) {
      return { label: 'Team arbeitet', variant: 'running' as const };
    }
    return { label: 'Bereit', variant: 'idle' as const };
  });

  // Operativer Status Badge
  function OperationalStatusBadge() {
    const status = operationalStatus.value;
    const variantClass =
      status.variant === 'locked'
        ? styles.statusLocked
        : status.variant === 'running'
          ? styles.statusRunning
          : styles.statusIdle;

    return (
      <span className={`${styles.operationalStatus} ${variantClass}`}>
        {status.variant === 'running' && <span className={styles.pulseIndicator} />}
        {status.variant === 'locked' && <Lock size={14} />}
        {status.label}
      </span>
    );
  }

  // Loading State
  if (!isInitialized.value || isLoading.value) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Loader2 size={32} className={styles.spinner} />
          <span>Lade Daten...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.dateLabel}>Heute</span>
          <OperationalStatusBadge />
        </div>
        <div className={styles.headerRight}>
          <RefreshButton />
          <SyncStatusIndicator />
        </div>
      </header>

      {/* Locked Banner */}
      {isLocked.value && (
        <div className={styles.lockedBanner}>
          <Lock size={16} />
          <span>Tagesabschluss durchgeführt - keine Bearbeitung möglich</span>
        </div>
      )}

      {/* GPS-Erfassung Indikator */}
      <GpsIndicator />

      {/* Main Content */}
      <main className={styles.main}>
        {currentStep.value === 'roster' && (
          <RosterSelector />
        )}

        {currentStep.value === 'running' && (
          <>
            <StopwatchControls />
            <ActiveCrewList />
          </>
        )}

        {currentStep.value === 'idle' && (
          <>
            <IdleControls />
            <ActiveCrewList />
          </>
        )}

        {currentStep.value === 'locked' && (
          <ActiveCrewList />
        )}
      </main>
    </div>
  );
}
